import { CUSTOM_ELEMENTS_SCHEMA, Component, OnInit, computed, inject, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import {
  REQUERIMENTO_TIPOS_BENEFICIO,
  getRequerimentoStatusCor,
  getRequerimentoTipoBeneficio,
} from '../../../core/constants/requerimento-online-opcoes';
import {
  RequerimentoOnlineFiltros,
  RequerimentoOnlineOpcao,
  RequerimentoOnlineOpcoesFiltro,
  RequerimentoOnlineSolicitacao,
} from '../../../core/models/requerimento-beneficio/requerimento-online-solicitacao.model';
import { RequerimentoOnlineSolicitacaoService } from '../../../core/services/requerimento-online-solicitacao.service';
import { ToastService } from '../../../core/services/toast.service';
import { formatDateIsoToBr } from '../../../core/utils/date-format.util';
import { EmptyStateComponent } from '../../../shared/components/empty-state/empty-state.component';
import { FilterBarComponent, FilterField } from '../../../shared/components/filter-bar/filter-bar.component';
import { MenuAcoesComponent, OpcaoMenuAcoes } from '../../../shared/components/menu-acoes/menu-acoes.component';
import { PaginationComponent } from '../../../shared/components/pagination/pagination.component';
import { StatusPieChartComponent, StatusPieChartSegment } from '../../../shared/components/status-pie-chart/status-pie-chart.component';
import { ApiHttpError } from '../../../shared/services/http-client/api-http-error.model';
import { RequerimentoStatusTagComponent } from '../../../shared/components/requerimento-status-tag/requerimento-status-tag.component';
import { LoaderService } from '../../../shared/services/loader/loader.service';

/** Ações do menu ⋮ de cada linha da tabela. */
const OPCOES_MENU: readonly OpcaoMenuAcoes[] = [{ id: 'visualizar', rotulo: 'Visualizar', icone: 'eye' }];

/** Tamanho inicial da página; o rodapé da tabela troca para 50 ou 100. */
const PAGE_SIZE = 10;

/** Cor no gráfico "Requerimentos por tipo de benefício" de cada label de `REQUERIMENTO_TIPOS_BENEFICIO`. */
const TIPO_BENEFICIO_CORES: Record<string, string> = {
  Aposentadoria: 'var(--color-primary-v500)',
  'Aposentadoria antecipada': 'var(--color-green-v500)',
  'Aposentadoria por invalidez': 'var(--color-red-v500)',
};

/** Cores dos tipos ou status que vierem fora das listas locais. */
const CORES_EXTRAS = ['var(--color-gray-v500)', 'var(--color-gray-v700)'];

/**
 * Uma fatia por opção, na ordem da lista e mesmo com total zero, cada uma com cor fixa. Valores
 * fora da lista entram no fim, com as cores de `CORES_EXTRAS`.
 */
function segmentosPor(
  opcoes: readonly { label: string }[],
  corDe: (label: string, indice: number) => string | undefined,
  valores: readonly string[],
): StatusPieChartSegment[] {
  const totais = new Map<string, number>(opcoes.map((opcao) => [opcao.label, 0]));
  for (const valor of valores) {
    totais.set(valor, (totais.get(valor) ?? 0) + 1);
  }

  let extras = 0;
  return [...totais].map(([label, value], indice) => ({
    label,
    value,
    color: corDe(label, indice) ?? CORES_EXTRAS[extras++ % CORES_EXTRAS.length],
  }));
}

/** Colunas da tabela que podem ser ordenadas pelo cabeçalho. */
export type ColunaOrdenavel = 'nome' | 'matricula' | 'tipoBeneficio' | 'dataSolicitacao' | 'status';
type DirecaoOrdenacao = 'asc' | 'desc';
interface Ordenacao {
  coluna: ColunaOrdenavel;
  direcao: DirecaoOrdenacao;
}

const compararTexto = (a: string, b: string): number =>
  a.localeCompare(b, 'pt-BR', { sensitivity: 'base', numeric: true });

/**
 * Comparação crescente da coluna: texto em ordem alfabética (sem diferenciar maiúsculas e
 * acentos), matrícula numérica e data pela ISO `yyyy-MM-dd`. Sem data fica por último.
 */
function compararPorColuna(
  a: RequerimentoOnlineSolicitacao,
  b: RequerimentoOnlineSolicitacao,
  coluna: ColunaOrdenavel,
): number {
  switch (coluna) {
    case 'nome':
      return compararTexto(a.nomeParticipante, b.nomeParticipante);
    case 'matricula':
      return a.matricula - b.matricula;
    case 'tipoBeneficio':
      return compararTexto(a.tipoBeneficio, b.tipoBeneficio);
    case 'status':
      return compararTexto(a.status, b.status);
    case 'dataSolicitacao':
      if (!a.dataSolicitacao || !b.dataSolicitacao) {
        return Number(!a.dataSolicitacao) - Number(!b.dataSolicitacao);
      }
      return a.dataSolicitacao.localeCompare(b.dataSolicitacao);
  }
}

/** Opções de select com "Todos" na frente: o código vai como valor do filtro. */
function toFilterOptions(opcoes: readonly RequerimentoOnlineOpcao[]): RequerimentoOnlineOpcao[] {
  return [{ value: 'todos', label: 'Todos' }, ...opcoes];
}

const SEM_OPCOES: RequerimentoOnlineOpcoesFiltro = { planos: [], beneficios: [], status: [] };

/**
 * A carga inicial da lista vem do `app-filter-bar`, que emite os filtros padrão ao iniciar; as opções dos combos
 * são carregadas em paralelo no `ngOnInit`.
 */
@Component({
  selector: 'app-solicitacoes-requerimento',
  imports: [
    NgTemplateOutlet,
    RequerimentoStatusTagComponent,
    EmptyStateComponent,
    FilterBarComponent,
    MenuAcoesComponent,
    PaginationComponent,
    StatusPieChartComponent,
  ],
  templateUrl: './solicitacoes-requerimento.component.html',
  styleUrl: './solicitacoes-requerimento.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SolicitacoesRequerimentoComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);
  private readonly requerimentoOnlineSolicitacaoService = inject(RequerimentoOnlineSolicitacaoService);
  private readonly solicitacoesSignal = signal<readonly RequerimentoOnlineSolicitacao[]>([]);
  readonly solicitacoes = this.solicitacoesSignal.asReadonly();
  /** Planos, benefícios e status dos combos; vazios até a API responder (ou se ela falhar). */
  readonly opcoes = signal<RequerimentoOnlineOpcoesFiltro>(SEM_OPCOES);
  readonly formatDate = formatDateIsoToBr;
  /** Filtros da última busca: com algum preenchido, o vazio fala dos filtros (como na integracao-gestao do perfil). */
  private readonly filtrosSignal = signal<RequerimentoOnlineFiltros>({});
  readonly filtroAplicado = computed(() => Object.keys(this.filtrosSignal()).length > 0);
  readonly currentPage = signal(1);
  readonly pageSize = signal(PAGE_SIZE);
  readonly opcoesMenu = OPCOES_MENU;
  /** Coluna e direção da ordenação da tabela; `null` mantém a ordem recebida. */
  readonly ordenacao = signal<Ordenacao | null>(null);
  /** A lista vem completa: ordenação e paginação são feitas aqui, nessa ordem. */
  readonly solicitacoesOrdenadas = computed(() => {
    const ordenacao = this.ordenacao();
    const lista = this.solicitacoesSignal();
    if (!ordenacao) {
      return lista;
    }

    const fator = ordenacao.direcao === 'asc' ? 1 : -1;
    return [...lista].sort((a, b) => fator * compararPorColuna(a, b, ordenacao.coluna));
  });
  readonly solicitacoesPaginadas = computed(() => {
    const inicio = (this.currentPage() - 1) * this.pageSize();
    return this.solicitacoesOrdenadas().slice(inicio, inicio + this.pageSize());
  });
  /** Indicadores e gráficos seguem a lista filtrada, como a tabela. */
  /** Total e, por tipo de benefício, a quantidade e o percentual (barra em linha, como as integrações da campanha). */
  readonly indicadores = computed(() => {
    const total = this.solicitacoesSignal().length;
    return {
      total: total.toLocaleString('pt-BR'),
      tipos: this.requerimentosPorTipoBeneficio().map((segmento) => ({
        ...segmento,
        valorTexto: segmento.value.toLocaleString('pt-BR'),
        percentual: total ? Math.round((segmento.value / total) * 100) : 0,
      })),
    };
  });
  /** Os benefícios do catálogo agrupados nos 3 tipos da PRE-328 (`getRequerimentoTipoBeneficio`). */
  readonly requerimentosPorTipoBeneficio = computed<readonly StatusPieChartSegment[]>(() =>
    segmentosPor(
      REQUERIMENTO_TIPOS_BENEFICIO,
      (label) => TIPO_BENEFICIO_CORES[label],
      this.solicitacoesSignal().map((s) => getRequerimentoTipoBeneficio(s.tipoBeneficio)),
    ),
  );
  /** Status na ordem e com os rótulos da API; a cor é a mesma das tags (`getRequerimentoStatusCor`). */
  readonly requerimentosPorStatus = computed<readonly StatusPieChartSegment[]>(() =>
    segmentosPor(
      this.opcoes().status,
      (label) => getRequerimentoStatusCor(label).cor,
      this.solicitacoesSignal().map((s) => s.status),
    ),
  );
  readonly filterFields = computed<FilterField[]>(() => [
    {
      key: 'nome',
      label: 'Procure por nome',
      type: 'vivest-input',
      placeholder: 'Digite o nome do participante',
    },
    {
      key: 'matricula',
      label: 'Procure por matrícula',
      type: 'vivest-input',
      placeholder: 'Digite a matrícula do participante',
    },
    {
      key: 'plano',
      label: 'Plano',
      type: 'vivest-autocomplete',
      // Sem opção "Todos": campo vazio é sem filtro de plano.
      placeholder: 'Todos os planos',
      options: this.opcoes().planos,
    },
    {
      key: 'tipo',
      label: 'Tipo de benefício',
      type: 'vivest-select',
      defaultValue: 'todos',
      options: toFilterOptions(this.opcoes().beneficios),
    },
    {
      key: 'status',
      label: 'Status',
      type: 'vivest-select',
      defaultValue: 'todos',
      options: toFilterOptions(this.opcoes().status),
    },
    {
      key: 'periodoDe',
      label: 'Período',
      type: 'daterange',
      keyTo: 'periodoAte',
    },
  ]);

  ngOnInit(): void {
    this.carregarOpcoes();
  }

  /** Nova quantidade por página volta para a primeira página. */
  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.currentPage.set(1);
  }

  onPageChange(page: number): void {
    this.currentPage.set(page);
  }

  /** Primeiro clique ordena a coluna de forma crescente; clicar de novo inverte a direção. */
  ordenarPor(coluna: ColunaOrdenavel): void {
    const atual = this.ordenacao();
    const direcao: DirecaoOrdenacao = atual?.coluna === coluna && atual.direcao === 'asc' ? 'desc' : 'asc';
    this.ordenacao.set({ coluna, direcao });
    this.currentPage.set(1);
  }

  /** Valor de `aria-sort` do cabeçalho da coluna. */
  ariaSort(coluna: ColunaOrdenavel): 'ascending' | 'descending' | 'none' {
    const ordenacao = this.ordenacao();
    if (ordenacao?.coluna !== coluna) {
      return 'none';
    }
    return ordenacao.direcao === 'asc' ? 'ascending' : 'descending';
  }

  /** Seta para cima na coluna sem ordenação (esmaecida) e na crescente; para baixo na decrescente. */
  iconeOrdenacao(coluna: ColunaOrdenavel): string {
    return this.ariaSort(coluna) === 'descending' ? 'arrow-down' : 'arrow-up';
  }

  onFiltersChange(record: Record<string, string>): void {
    this.currentPage.set(1);

    const filtros: RequerimentoOnlineFiltros = {};

    const nome = (record['nome'] ?? '').trim();
    if (nome) {
      filtros.nome = nome;
    }

    const matricula = (record['matricula'] ?? '').trim();
    if (matricula) {
      filtros.matricula = matricula;
    }

    if (record['plano'] && record['plano'] !== 'todos') {
      filtros.codPlano = record['plano'];
    }

    if (record['tipo'] && record['tipo'] !== 'todos') {
      filtros.idBeneficio = Number(record['tipo']);
    }

    if (record['status'] && record['status'] !== 'todos') {
      filtros.tpStatus = record['status'];
    }

    if (record['periodoDe']) {
      filtros.periodoDe = record['periodoDe'];
    }

    if (record['periodoAte']) {
      filtros.periodoAte = record['periodoAte'];
    }

    this.filtrosSignal.set(filtros);
    this.listarSolicitacoes(filtros);
  }

  /** Opção escolhida no menu ⋮ da linha; "Visualizar" abre o detalhe da solicitação. */
  onAcao(solicitacao: RequerimentoOnlineSolicitacao, opcao: string): void {
    if (opcao === 'visualizar') {
      void this.router.navigate(['/requerimento-online/detalhe', solicitacao.idSolicitacao]);
    }
  }

  /** Sem as opções, a tela segue com a lista; só os combos e os cards por benefício ficam vazios. */
  private carregarOpcoes(): void {
    this.requerimentoOnlineSolicitacaoService.listarOpcoesFiltro().subscribe({
      next: (opcoes) => this.opcoes.set(opcoes),
      error: () => this.toastService.show('Não foi possível carregar as opções dos filtros.', 'error'),
    });
  }

  private listarSolicitacoes(filtros: RequerimentoOnlineFiltros): void {
    this.loaderService.showLoader();

    this.requerimentoOnlineSolicitacaoService
      .listarSolicitacoes(filtros)
      .pipe(finalize(() => this.loaderService.hideLoader()))
      .subscribe({
        next: (lista) => this.solicitacoesSignal.set(lista),
        error: (error: unknown) => {
          this.solicitacoesSignal.set([]);
          const message =
            error instanceof ApiHttpError
              ? error.message
              : 'Não foi possível carregar as solicitações.';
          this.toastService.show(message, 'error');
        },
      });
  }
}
